import { FieldValues } from 'react-hook-form'
import { FieldType, IFormField } from 'types'
import {
  ControlledInput,
  ControlledSelect,
  ControlledMultiSelect,
  ControlledCheckbox,
  ControlledRadio,
  ControlledDatePicker,
  ControlledTextarea,
  ControlledFileUpload
} from '../ControlledFormElements'

type FieldRendererProps<T extends FieldValues> = {
  field: IFormField<T>
  disabled?: boolean
  readonly?: boolean
}

export const FieldRenderer = <T extends FieldValues>({
  field,
  readonly
}: FieldRendererProps<T>) => {
  const disabled = readonly ?? field.disabled

  if (!('type' in field) || field.type === undefined) {
    return (
      <ControlledInput<T>
        {...field}
        type={field.htmlType}
        disabled={disabled}
      />
    )
  }

  switch (field.type) {
    case FieldType.INPUT:
      return (
        <ControlledInput<T>
          {...field}
          type={field.htmlType}
          disabled={disabled}
        />
      )
    case FieldType.SELECT:
      return <ControlledSelect<T> {...field} disabled={disabled} />
    case FieldType.MULTI_SELECT:
      return <ControlledMultiSelect<T> {...field} disabled={disabled} />
    case FieldType.CHECKBOX:
      return <ControlledCheckbox<T> {...field} disabled={disabled} />
    case FieldType.RADIO:
      return <ControlledRadio<T> {...field} disabled={disabled} />
    case FieldType.DATE:
    case FieldType.DATE_TIME:
      return <ControlledDatePicker<T> {...field} disabled={disabled} />
    case FieldType.TEXTAREA:
      return <ControlledTextarea<T> {...field} disabled={disabled} />
    case FieldType.FILE:
      return <ControlledFileUpload<T> {...field} disabled={disabled} />
    case FieldType.CUSTOM: {
      const { CustomComponent, customComponentProps = {} } = field
      return (
        <CustomComponent
          {...customComponentProps}
          {...field}
          disabled={disabled}
          readonly={readonly}
        />
      )
    }
    default:
      throw new Error(`Invalid field type: ${field?.type}`)
  }
}
